/**
 * OLA COSMETICS — Unified Theme JS
 * Wonder + Dynamic theme features
 * RTL Hebrew | Shopify 2.0
 *
 * All data rendered from Shopify's own trusted API endpoints.
 * Text content uses textContent (not innerHTML) to prevent XSS.
 * Structural HTML templates use createElement/appendChild.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────
     HELPERS
     ───────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  function debounce(fn, ms = 100) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  function formatMoney(cents) {
    return '\u20AA' + Math.round(cents / 100);
  }

  /** Create element helper — safer than innerHTML for unknown content */
  function el(tag, props = {}, ...children) {
    const node = document.createElement(tag);
    Object.entries(props).forEach(([k, v]) => {
      if (k === 'class') node.className = v;
      else if (k === 'text') node.textContent = v;
      else if (k.startsWith('data-')) node.setAttribute(k, v);
      else node[k] = v;
    });
    children.forEach(child => {
      if (child == null) return;
      node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    });
    return node;
  }

  /* ─────────────────────────────────────────
     1. SCROLL REVEAL
     ───────────────────────────────────────── */
  function initScrollReveal() {
    const els = $$('[data-reveal]');
    if (!els.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    els.forEach(e => observer.observe(e));
  }

  /* ─────────────────────────────────────────
     2. PARALLAX SCROLL
     ───────────────────────────────────────── */
  function initParallax() {
    const banners = $$('[data-parallax-bg]');
    if (!banners.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(max-width: 767px)').matches) return;

    function onScroll() {
      banners.forEach(banner => {
        const bg = banner.querySelector('.parallax-banner__bg') || banner;
        const rect = banner.getBoundingClientRect();
        const viewH = window.innerHeight;
        if (rect.bottom < 0 || rect.top > viewH) return;
        const progress = (viewH - rect.top) / (viewH + rect.height);
        const shift = (progress - 0.5) * 80;
        bg.style.transform = 'translateY(' + shift + 'px)';
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ─────────────────────────────────────────
     3. STICKY HEADER SHRINK
     ───────────────────────────────────────── */
  function initStickyHeader() {
    const header = $('[data-header]');
    if (!header) return;

    function onScroll() {
      const scrolled = window.scrollY > 60;
      header.classList.toggle('site-header--scrolled', scrolled);
      header.classList.toggle('site-header--shrunk', scrolled);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ─────────────────────────────────────────
     4. ANNOUNCEMENT BAR ROTATION
     ───────────────────────────────────────── */
  function initAnnouncementBar() {
    const track = $('#AnnTrack');
    if (!track) return;

    const items = $$('.announcement-bar__item', track);
    if (items.length <= 1) return;

    let current = 0;
    let timer;

    function show(idx) {
      items[current].classList.remove('announcement-bar__item--active');
      current = (idx + items.length) % items.length;
      items[current].classList.add('announcement-bar__item--active');
    }

    function autoPlay() {
      timer = setInterval(() => show(current + 1), 4000);
    }

    on($('#AnnNext'), 'click', () => { clearInterval(timer); show(current + 1); autoPlay(); });
    on($('#AnnPrev'), 'click', () => { clearInterval(timer); show(current - 1); autoPlay(); });

    autoPlay();
  }

  /* ─────────────────────────────────────────
     5. MOBILE MENU DRAWER
     ───────────────────────────────────────── */
  function initMobileMenu() {
    const toggleBtn = $('[data-menu-toggle]');
    const drawer = $('.site-header__mobile-drawer');
    const overlay = $('#SiteOverlay');
    const closeBtn = drawer && $('[data-menu-close]', drawer);

    if (!toggleBtn || !drawer) return;

    function openMenu() {
      drawer.classList.add('site-header__mobile-drawer--open');
      overlay && overlay.classList.add('overlay--active');
      document.body.style.overflow = 'hidden';
    }

    function closeMenu() {
      drawer.classList.remove('site-header__mobile-drawer--open');
      overlay && overlay.classList.remove('overlay--active');
      document.body.style.overflow = '';
    }

    on(toggleBtn, 'click', openMenu);
    on(closeBtn, 'click', closeMenu);
    on(overlay, 'click', closeMenu);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeMenu(); });
  }

  /* ─────────────────────────────────────────
     6. CART DRAWER (Ajax)
     ───────────────────────────────────────── */
  const CartDrawer = {
    el: null,
    overlay: null,

    init() {
      this.el = $('#CartDrawer') || $('.cart-drawer');
      this.overlay = $('#SiteOverlay');
      if (!this.el) return;

      on($('[data-cart-toggle]'), 'click', (e) => { e.preventDefault(); this.open(); });
      on(this.el.querySelector('[data-cart-close]'), 'click', () => this.close());
      on(this.overlay, 'click', () => this.close());
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

      on(this.el, 'click', (e) => {
        if (e.target.closest('[data-qty-minus]')) this.updateQty(e, -1);
        if (e.target.closest('[data-qty-plus]'))  this.updateQty(e,  1);
        if (e.target.closest('[data-remove-item]')) this.removeItem(e);
      });

      document.addEventListener('cart:updated', () => this.refresh());
    },

    open() {
      this.el.classList.add('cart-drawer--open');
      this.overlay && this.overlay.classList.add('overlay--active');
      document.body.style.overflow = 'hidden';
      this.refresh();
    },

    close() {
      this.el.classList.remove('cart-drawer--open');
      this.overlay && this.overlay.classList.remove('overlay--active');
      document.body.style.overflow = '';
    },

    async refresh() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this.render(cart);
      } catch (e) {
        console.warn('Cart refresh failed', e);
      }
    },

    render(cart) {
      const count = cart.item_count || 0;

      /* Update all cart count badges */
      $$('.site-header__cart-count').forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      });

      /* Shipping bar */
      const THRESHOLD = 24900;
      const pct = Math.min(100, (cart.total_price / THRESHOLD) * 100);
      const fill = this.el.querySelector('.cart-drawer__shipping-fill');
      const shippingText = this.el.querySelector('.cart-drawer__shipping-text');
      if (fill) fill.style.width = pct + '%';
      if (shippingText) {
        shippingText.textContent = pct >= 100
          ? 'יש לך משלוח חינם!'
          : ('עוד ' + formatMoney(THRESHOLD - cart.total_price) + ' למשלוח חינם');
      }

      const itemsEl = this.el.querySelector('.cart-drawer__items');
      const emptyEl = this.el.querySelector('.cart-drawer__empty');
      const footerEl = this.el.querySelector('.cart-drawer__footer');
      if (!itemsEl) return;

      if (count === 0) {
        while (itemsEl.firstChild) itemsEl.removeChild(itemsEl.firstChild);
        emptyEl && (emptyEl.style.display = 'flex');
        footerEl && (footerEl.style.display = 'none');
        return;
      }

      emptyEl && (emptyEl.style.display = 'none');
      footerEl && (footerEl.style.display = 'flex');
      while (itemsEl.firstChild) itemsEl.removeChild(itemsEl.firstChild);

      cart.items.forEach(item => {
        const link = el('a', { href: item.url, class: 'cart-item__img-wrap' });
        const img = el('img', { width: 80, height: 80, loading: 'lazy', alt: item.product_title });
        img.src = item.image || '';
        link.appendChild(img);

        const info = el('div', { class: 'cart-item__info' });
        info.appendChild(el('p', { class: 'cart-item__title', text: item.product_title }));
        if (item.variant_title && item.variant_title !== 'Default Title') {
          info.appendChild(el('p', { class: 'cart-item__variant', text: item.variant_title }));
        }

        const qtyWrap = el('div', { class: 'cart-item__qty' });
        const minusBtn = el('button', { class: 'cart-item__qty-btn', 'data-qty-minus': '', 'aria-label': 'פחות', text: '−' });
        const qtyVal = el('span', { class: 'cart-item__qty-value', text: String(item.quantity) });
        const plusBtn = el('button', { class: 'cart-item__qty-btn', 'data-qty-plus': '', 'aria-label': 'יותר', text: '+' });
        qtyWrap.append(minusBtn, qtyVal, plusBtn);
        info.appendChild(qtyWrap);

        const rightCol = el('div', {});
        rightCol.appendChild(el('p', { class: 'cart-item__price', text: formatMoney(item.final_line_price) }));
        const removeBtn = el('button', { class: 'cart-item__remove', 'data-remove-item': '', 'aria-label': 'הסר' });
        const removeSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        removeSvg.setAttribute('viewBox', '0 0 24 24');
        removeSvg.setAttribute('fill', 'none');
        removeSvg.setAttribute('stroke', 'currentColor');
        removeSvg.setAttribute('stroke-width', '1.5');
        removeSvg.setAttribute('width', '14');
        removeSvg.setAttribute('height', '14');
        const removePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        removePath.setAttribute('d', 'M18 6L6 18M6 6l12 12');
        removeSvg.appendChild(removePath);
        removeBtn.appendChild(removeSvg);
        rightCol.appendChild(removeBtn);

        const row = el('div', { class: 'cart-item', 'data-item-key': item.key });
        row.append(link, info, rightCol);
        itemsEl.appendChild(row);
      });

      const totalEl = this.el.querySelector('.cart-drawer__total-amount');
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
    },

    async updateQty(e, delta) {
      const item = e.target.closest('.cart-item');
      if (!item) return;
      const key = item.dataset.itemKey;
      const qtyEl = item.querySelector('.cart-item__qty-value');
      const newQty = Math.max(0, parseInt(qtyEl.textContent) + delta);
      await this.setQty(key, newQty);
    },

    async removeItem(e) {
      const item = e.target.closest('.cart-item');
      if (!item) return;
      await this.setQty(item.dataset.itemKey, 0);
    },

    async setQty(key, qty) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
        const cart = await res.json();
        this.render(cart);
      } catch (e) {
        console.warn('Cart update failed', e);
      }
    }
  };

  /* ─────────────────────────────────────────
     7. AJAX ADD TO CART
     ───────────────────────────────────────── */
  function initAjaxATC() {
    on(document, 'click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();

      const variantId = btn.dataset.variantId || btn.dataset.quickAdd;
      if (!variantId) return;

      btn.disabled = true;
      const orig = btn.textContent;
      btn.textContent = 'מוסיף...';

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId), quantity: 1 }),
        });
        btn.textContent = '\u2713 נוסף';
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:item-added'));
        setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 2000);
      } catch {
        btn.textContent = orig;
        btn.disabled = false;
      }
    });

    on(document, 'submit', async (e) => {
      const form = e.target.closest('[data-product-form]');
      if (!form) return;
      e.preventDefault();

      const btn = form.querySelector('[data-atc-btn]') || form.querySelector('[type="submit"]');
      if (!btn) return;

      const formData = new FormData(form);
      btn.disabled = true;
      const origText = btn.textContent;
      btn.textContent = 'מוסיף...';

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: formData.get('id'), quantity: parseInt(formData.get('quantity') || 1) }),
        });
        btn.textContent = '\u2713 נוסף לעגלה';
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:item-added'));
        setTimeout(() => { btn.textContent = origText; btn.disabled = false; }, 2500);
      } catch {
        btn.textContent = origText;
        btn.disabled = false;
      }
    });
  }

  /* ─────────────────────────────────────────
     9. BEFORE / AFTER DRAG SLIDER
     ───────────────────────────────────────── */
  function initBeforeAfter() {
    $$('.ba-slider').forEach(slider => {
      const afterImg = slider.querySelector('.ba-slider__after');
      const handle = slider.querySelector('.ba-slider__handle');
      if (!afterImg || !handle) return;

      let dragging = false;

      function setPosition(clientX) {
        const rect = slider.getBoundingClientRect();
        let pct = ((clientX - rect.left) / rect.width) * 100;
        pct = Math.max(2, Math.min(98, pct));
        afterImg.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
        handle.style.left = pct + '%';
      }

      on(slider, 'mousedown', (e) => { dragging = true; setPosition(e.clientX); });
      on(slider, 'touchstart', (e) => { dragging = true; setPosition(e.touches[0].clientX); }, { passive: true });
      on(document, 'mousemove', (e) => { if (dragging) setPosition(e.clientX); });
      on(document, 'touchmove', (e) => { if (dragging) setPosition(e.touches[0].clientX); }, { passive: true });
      on(document, 'mouseup', () => { dragging = false; });
      on(document, 'touchend', () => { dragging = false; });

      /* init at center */
      const r = slider.getBoundingClientRect();
      setPosition(r.left + r.width / 2);
    });
  }

  /* ─────────────────────────────────────────
     10. CAROUSELS — scroll with arrows + touch
     ───────────────────────────────────────── */
  function initCarousels() {
    $$('[data-carousel]').forEach(carousel => {
      const track = carousel.querySelector('[data-carousel-track]')
        || carousel.querySelector('.reels-track')
        || carousel.querySelector('.testimonials-carousel')
        || carousel.querySelector('.featured-products__carousel');
      const prevBtn = carousel.querySelector('[data-carousel-prev]');
      const nextBtn = carousel.querySelector('[data-carousel-next]');
      if (!track) return;

      function getCardWidth() {
        const first = track.children[0];
        if (!first) return 280;
        const gap = parseInt(getComputedStyle(track).columnGap) || 16;
        return first.offsetWidth + gap;
      }

      on(prevBtn, 'click', () => {
        const d = document.documentElement.dir === 'rtl';
        track.scrollBy({ left: d ? getCardWidth() : -getCardWidth(), behavior: 'smooth' });
      });
      on(nextBtn, 'click', () => {
        const d = document.documentElement.dir === 'rtl';
        track.scrollBy({ left: d ? -getCardWidth() : getCardWidth(), behavior: 'smooth' });
      });

      let startX = 0, startScroll = 0;
      on(track, 'touchstart', (e) => { startX = e.touches[0].clientX; startScroll = track.scrollLeft; }, { passive: true });
      on(track, 'touchmove', (e) => {
        const dx = startX - e.touches[0].clientX;
        track.scrollLeft = startScroll + (document.documentElement.dir === 'rtl' ? -dx : dx);
      }, { passive: true });
    });
  }

  /* ─────────────────────────────────────────
     11. PRODUCT GALLERY THUMBNAILS
     ───────────────────────────────────────── */
  function initProductGallery() {
    $$('.product-gallery').forEach(gallery => {
      const mainImg = gallery.querySelector('.product-gallery__main img');
      const thumbs = $$('.product-gallery__thumb', gallery);
      if (!mainImg || !thumbs.length) return;

      thumbs.forEach(thumb => {
        on(thumb, 'click', () => {
          const src = thumb.querySelector('img')?.src;
          if (src) mainImg.src = src.replace(/_(pico|icon|thumb|small|compact|medium|large|grande)/, '');
          thumbs.forEach(t => t.classList.remove('product-gallery__thumb--active'));
          thumb.classList.add('product-gallery__thumb--active');
        });
      });
    });
  }

  /* ─────────────────────────────────────────
     12. QUICK VIEW MODAL
     ───────────────────────────────────────── */
  const QuickView = {
    overlay: null,

    init() {
      this.overlay = $('#QuickViewOverlay');
      if (!this.overlay) return;

      on($('#QuickViewClose'), 'click', () => this.close());
      on(this.overlay, 'click', (e) => { if (e.target === this.overlay) this.close(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

      on(document, 'click', (e) => {
        const btn = e.target.closest('[data-quick-view]');
        if (btn) this.open(btn.dataset.quickView);
      });
    },

    open(handle) {
      if (!handle || !this.overlay) return;
      this.overlay.classList.add('quick-view-overlay--open');
      document.body.style.overflow = 'hidden';
      this.load(handle);
    },

    close() {
      if (!this.overlay) return;
      this.overlay.classList.remove('quick-view-overlay--open');
      document.body.style.overflow = '';
    },

    async load(handle) {
      const galleryEl = $('#QuickViewGallery');
      const infoEl = $('#QuickViewInfo');
      if (!galleryEl || !infoEl) return;

      while (infoEl.firstChild) infoEl.removeChild(infoEl.firstChild);
      while (galleryEl.firstChild) galleryEl.removeChild(galleryEl.firstChild);

      const loading = el('div', { style: 'padding:2rem;text-align:center;opacity:.5', text: 'טוענת...' });
      infoEl.appendChild(loading);

      try {
        const res = await fetch('/products/' + handle + '.js');
        const product = await res.json();
        const variant = product.variants[0];

        while (galleryEl.firstChild) galleryEl.removeChild(galleryEl.firstChild);
        while (infoEl.firstChild) infoEl.removeChild(infoEl.firstChild);

        if (product.images[0]) {
          const img = el('img', { width: 450, height: 450, alt: product.title });
          img.src = product.images[0].replace(/_(pico|icon|thumb|small|compact|medium|large)/, '');
          galleryEl.appendChild(img);
        }

        infoEl.appendChild(el('h2', { class: 'quick-view-modal__title', text: product.title }));
        infoEl.appendChild(el('p', { class: 'quick-view-modal__price', text: formatMoney(variant.price) }));

        const desc = el('p', { class: 'quick-view-modal__desc' });
        desc.textContent = product.description.replace(/<[^>]+>/g, '').substring(0, 200) + '...';
        infoEl.appendChild(desc);

        const atcBtn = el('button', {
          class: 'btn btn--dark btn--full',
          'data-quick-add': String(variant.id),
          'data-variant-id': String(variant.id),
          text: 'הוסיפי לעגלה'
        });
        infoEl.appendChild(atcBtn);

        const link = el('a', { href: '/products/' + handle, class: 'quick-view-modal__link', text: 'לדף המוצר המלא' });
        infoEl.appendChild(link);
      } catch {
        while (infoEl.firstChild) infoEl.removeChild(infoEl.firstChild);
        infoEl.appendChild(el('div', { style: 'padding:2rem', text: 'שגיאה בטעינה' }));
      }
    }
  };

  /* ─────────────────────────────────────────
     13. ACCORDION
     ───────────────────────────────────────── */
  function initAccordions() {
    on(document, 'click', (e) => {
      const header = e.target.closest('.accordion-item__header');
      if (!header) return;
      const item = header.closest('.accordion-item');
      if (!item) return;
      const isOpen = item.classList.contains('accordion-item--open');

      const group = item.closest('.accordion-group');
      if (group) {
        $$('.accordion-item--open', group).forEach(openItem => {
          if (openItem !== item) openItem.classList.remove('accordion-item--open');
        });
      }
      item.classList.toggle('accordion-item--open', !isOpen);
    });
  }

  /* ─────────────────────────────────────────
     14. STICKY ATC BAR
     ───────────────────────────────────────── */
  function initStickyATC() {
    const bar = $('#StickyATC') || $('.sticky-atc-bar');
    const mainBtn = $('[data-main-atc]');
    if (!bar || !mainBtn) return;

    const observer = new IntersectionObserver((entries) => {
      bar.classList.toggle('sticky-atc-bar--visible', !entries[0].isIntersecting);
    }, { threshold: 0 });
    observer.observe(mainBtn);

    on(bar.querySelector('[data-sticky-atc-btn]'), 'click', () => mainBtn.click());
  }

  /* ─────────────────────────────────────────
     15. RECENTLY VIEWED (localStorage)
     ───────────────────────────────────────── */
  const RecentlyViewed = {
    KEY: 'ola_rv',
    MAX: 6,
    get() {
      try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); } catch { return []; }
    },
    add(product) {
      if (!product || !product.id) return;
      let list = this.get().filter(p => p.id !== product.id);
      list.unshift(product);
      if (list.length > this.MAX) list = list.slice(0, this.MAX);
      try { localStorage.setItem(this.KEY, JSON.stringify(list)); } catch {}
    },
    render(containerSelector) {
      const container = $(containerSelector);
      if (!container) return;
      const list = this.get();
      if (!list.length) { container.closest('.recently-viewed')?.remove(); return; }

      while (container.firstChild) container.removeChild(container.firstChild);

      list.forEach(p => {
        const card = el('a', { href: '/products/' + p.handle, class: 'fp-card' });
        card.style.textDecoration = 'none';

        const imgWrap = el('div', { class: 'fp-card__image-wrap' });
        const img = el('img', { loading: 'lazy', width: 260, height: 346, alt: p.title });
        img.src = p.image || '';
        const atcBtn = el('button', {
          class: 'fp-card__quick-add',
          'data-quick-add': String(p.variant_id),
          'data-variant-id': String(p.variant_id),
          text: 'הוסיפי לעגלה'
        });
        imgWrap.append(img, atcBtn);

        const info = el('div', { class: 'fp-card__info' });
        info.appendChild(el('p', { class: 'fp-card__title', text: p.title }));
        info.appendChild(el('p', { class: 'fp-card__price', text: p.price }));

        card.append(imgWrap, info);
        container.appendChild(card);
      });
    }
  };

  function trackProductView() {
    const meta = window.__OLA_PRODUCT__;
    if (!meta) return;
    RecentlyViewed.add(meta);
    RecentlyViewed.render('[data-recently-viewed]');
  }

  /* ─────────────────────────────────────────
     16. SEARCH TOGGLE
     ───────────────────────────────────────── */
  function initSearch() {
    const btn = $('[data-search-toggle]');
    const overlay = $('#SearchOverlay');
    if (!btn || !overlay) return;

    on(btn, 'click', () => {
      const open = overlay.hasAttribute('hidden');
      overlay.toggleAttribute('hidden', !open);
      if (open) overlay.querySelector('input')?.focus();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') overlay.setAttribute('hidden', '');
    });
  }

  /* ─────────────────────────────────────────
     17. VARIANT PICKER
     ───────────────────────────────────────── */
  function initVariantPicker() {
    $$('[data-variant-form]').forEach(form => {
      const select = form.querySelector('select[name="id"]');
      if (!select) return;
      const wrapper = form.closest('[data-product-wrapper]');
      const priceEl = wrapper && wrapper.querySelector('[data-product-price]');
      const atcBtn = form.querySelector('[data-atc-btn]') || form.querySelector('[type="submit"]');

      on(select, 'change', () => {
        const option = select.options[select.selectedIndex];
        if (priceEl && option.dataset.price) priceEl.textContent = formatMoney(parseInt(option.dataset.price));
        if (atcBtn) {
          const available = option.dataset.available !== 'false';
          atcBtn.disabled = !available;
          atcBtn.textContent = available ? 'הוסיפי לעגלה' : 'אזל המלאי';
        }
      });
    });

    on(document, 'click', (e) => {
      const swatch = e.target.closest('[data-swatch]');
      if (!swatch) return;
      const form = swatch.closest('[data-variant-form]') || swatch.closest('form');
      const select = form && form.querySelector('select[name="id"]');
      if (select) { select.value = swatch.dataset.swatch; select.dispatchEvent(new Event('change')); }
      swatch.closest('[data-swatches]')?.querySelectorAll('[data-swatch]').forEach(s => s.classList.remove('active'));
      swatch.classList.add('active');
    });
  }

  /* ─────────────────────────────────────────
     18. REEL VIDEO PLAY
     ───────────────────────────────────────── */
  function initReelVideos() {
    on(document, 'click', (e) => {
      const playBtn = e.target.closest('.reel-card__play-btn');
      if (!playBtn) return;
      const media = playBtn.closest('.reel-card__media');
      const video = media && media.querySelector('video');
      if (video) {
        playBtn.style.display = 'none';
        video.setAttribute('controls', '');
        video.play().catch(() => {});
      }
    });
  }

  /* ─────────────────────────────────────────
     INIT
     ───────────────────────────────────────── */
  function init() {
    initScrollReveal();
    initParallax();
    initStickyHeader();
    initAnnouncementBar();
    initMobileMenu();
    CartDrawer.init();
    initAjaxATC();
    initBeforeAfter();
    initCarousels();
    initProductGallery();
    QuickView.init();
    initAccordions();
    initStickyATC();
    trackProductView();
    initSearch();
    initVariantPicker();
    initReelVideos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.OLA = { CartDrawer, QuickView, RecentlyViewed, formatMoney };
})();
